(function pfgSw1BuilderV3App(global, document) {
  'use strict';

  const VERSION = '3.37.0';
  const ROOT_SELECTOR = '[data-pfg-sw1-builder-v3]';
  const IMAGE_CACHE_LIMIT = 8;
  const IMAGE_LOAD_TIMEOUT_MS = 15000;
  const previousApp = global.PFGSw1BuilderV3;
  const catalog = global.PFGSw1BuilderV3Catalog;
  const manifest = global.PFGSw1BuilderV3Manifest;
  const core = global.PFGSw1BuilderV3Core;

  function rootsIn(scope = document) {
    if (!scope) return [];
    if (scope.matches?.(ROOT_SELECTOR)) return [scope];
    return Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);
  }

  function revealStaticFallback(root) {
    const fallback = root?.querySelector('[data-pfg-v3-fallback]');
    if (!fallback) return;
    const fallbackUrl = fallback.dataset.fallbackSrc;
    if (fallbackUrl && !fallback.getAttribute('src')) fallback.setAttribute('src', fallbackUrl);
    fallback.hidden = false;
  }

  function labelStaticFallback(root) {
    const stage = root?.querySelector('[data-pfg-v3-stage]');
    const summary = root?.querySelector('[data-pfg-v3-summary]');
    if (stage) stage.setAttribute('aria-label', 'Static white Switch 1 Joy-Con shell reference; configured preview unavailable');
    if (summary) summary.textContent = 'Static white shell reference only. No configured preview is currently available.';
  }

  function settleRootFailure(root, message) {
    if (!root) return;
    const stage = root.querySelector('[data-pfg-v3-stage]');
    const stack = root.querySelector('[data-pfg-v3-stack]');
    const loading = root.querySelector('[data-pfg-v3-loading]');
    const errorBox = root.querySelector('[data-pfg-v3-error]');
    const status = root.querySelector('[data-pfg-v3-status]');
    const submit = root.querySelector('.pfg-sw1-builder-v3__submit');
    const hasCommittedStack = Boolean(stack?.childElementCount);
    if (loading) loading.hidden = true;
    if (stage) stage.setAttribute('aria-busy', 'false');
    if (errorBox) {
      errorBox.textContent = message;
      errorBox.hidden = false;
    }
    if (stack) stack.hidden = !hasCommittedStack;
    if (hasCommittedStack) {
      const fallback = root.querySelector('[data-pfg-v3-fallback]');
      if (fallback) fallback.hidden = true;
    } else {
      revealStaticFallback(root);
      labelStaticFallback(root);
    }
    if (status) status.textContent = hasCommittedStack
      ? `${message} The previous valid preview remains visible.`
      : `${message} A static fallback remains visible.`;
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
    }
    root.dataset.pfgV3Ready = hasCommittedStack ? 'true' : 'false';
  }

  // Theme-editor scripts may be evaluated repeatedly. Dispose the prior module
  // before installing this closure so listeners/caches cannot survive reloads.
  try {
    if (typeof previousApp?.dispose === 'function') previousApp.dispose();
    else if (typeof previousApp?.destroy === 'function') previousApp.destroy(document);
  } catch (error) {
    console.warn('PFG SW1 Builder V3: prior draft cleanup failed.', error);
  }

  if (!catalog || !manifest || !core) {
    const message = 'Preview dependencies did not load. Checkout remains disabled.';
    rootsIn(document).forEach((root) => settleRootFailure(root, message));
    console.error('PFG SW1 Builder V3: catalog, manifest, and core must load before the app.');
    return;
  }

  const instances = new Map();
  const renderers = new Set();
  const moduleAbort = new AbortController();
  function stableSignature(parts) {
    const value = parts.join('|');
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
  }
  const contractSignature = stableSignature([
    VERSION, catalog.version, manifest.version, core.version,
    manifest.coordinateSpace?.id, manifest.coordinateSpace?.width, manifest.coordinateSpace?.height,
    ...Object.values(manifest.assets || {}).flatMap((asset) => [asset.id, asset.file, asset.slot, asset.sourcePack])
  ]);
  let moduleDisposed = false;
  let svgSequence = 0;

  function registeredImage(asset, loaded) {
    if (!asset.fragments && !asset.photoTransform) return loaded.cloneNode(false);
    const ns = 'http://www.w3.org/2000/svg';
    const make = (tag, attributes = {}) => {
      const node = document.createElementNS(ns, tag);
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
      return node;
    };
    const svg = make('svg', { viewBox: '0 0 1600 1600', width: 1600, height: 1600 });
    const defs = make('defs');
    svg.append(defs);
    const photoClip = asset.clipSide === 'left' ? 'M0 0H800V1600H0Z' : 'M800 0H1600V1600H800Z';
    const fragments = asset.fragments || [{ transform: asset.photoTransform, clipPath: photoClip }];
    fragments.forEach((fragment) => {
      const group = make('g', { transform: `matrix(${fragment.transform.join(' ')})` });
      const image = make('image', { href: loaded.currentSrc || loaded.src,
        x: 0, y: 0, width: asset.sourceSize?.[0] || 1600, height: asset.sourceSize?.[1] || 1600, preserveAspectRatio: 'none' });
      if (fragment.clipPath) {
        const id = `pfg-sw1-component-${++svgSequence}`;
        const clip = make('clipPath', { id, clipPathUnits: 'userSpaceOnUse' });
        clip.append(make('path', { d: fragment.clipPath }));
        defs.append(clip);
        image.setAttribute('clip-path', `url(#${id})`);
      }
      if (fragment.coverageRadius && fragment.coverageClipPath) {
        const id = `pfg-sw1-coverage-${++svgSequence}`;
        const clip = make('clipPath', { id, clipPathUnits: 'userSpaceOnUse' });
        clip.append(make('path', { d: fragment.coverageClipPath }));
        const filterId = `${id}-dilate`;
        const filter = make('filter', { id: filterId, x: '-20%', y: '-20%', width: '140%', height: '140%' });
        filter.append(make('feMorphology', { operator: 'dilate', radius: fragment.coverageRadius }));
        defs.append(clip, filter);
        // Expand only the clipped donor bumper, then bound it to the native
        // shoulder edge. The original photograph retains its detail above.
        const clipped = make('g', { 'clip-path': `url(#${id})` });
        const filtered = make('g', { filter: `url(#${filterId})` });
        filtered.append(image.cloneNode(false));
        clipped.append(filtered);
        group.append(clipped);
      }
      group.append(image);
      svg.append(group);
    });
    return svg;
  }

  class StalePreviewRequest extends Error {
    constructor(message = 'A newer preview request replaced this one.') {
      super(message);
      this.name = 'StalePreviewRequest';
    }
  }

  class AssetRequestCancelled extends StalePreviewRequest {
    constructor() {
      super('An obsolete image request was cancelled.');
      this.name = 'AssetRequestCancelled';
    }
  }

  class LayerStackRenderer {
    constructor(stack, assetMap, options = {}) {
      this.stack = stack;
      this.assetMap = assetMap;
      this.sequence = core.createSequenceGate();
      this.cache = new Map();
      this.pendingLoads = new Map();
      this.cacheLimit = Math.max(1, Number(options.cacheLimit) || IMAGE_CACHE_LIMIT);
      this.loadTimeoutMs = Math.max(20, Number(options.loadTimeoutMs) || IMAGE_LOAD_TIMEOUT_MS);
      this.disposed = false;
      renderers.add(this);
    }

    assetUrl(asset) {
      return this.assetMap[asset.baseId || asset.id] || this.assetMap[asset.file] || '';
    }

    touch(url, entry) {
      if (this.cache.get(url) !== entry) return;
      this.cache.delete(url);
      this.cache.set(url, entry);
    }

    evictOverflow() {
      while (this.cache.size > this.cacheLimit) {
        const oldestUrl = this.cache.keys().next().value;
        const oldest = this.cache.get(oldestUrl);
        this.cache.delete(oldestUrl);
        if (oldest && !oldest.settled) oldest.cancel();
      }
    }

    loadAsset(asset) {
      const url = this.assetUrl(asset);
      if (!url) return Promise.reject(new Error(`Missing URL for ${asset.description}.`));
      const cached = this.cache.get(url);
      if (cached) {
        this.touch(url, cached);
        return cached.promise;
      }
      const pending = this.pendingLoads.get(url);
      if (pending) return pending.promise;

      const entry = { promise: null, image: null, settled: false, cancel: () => {} };
      const rawRequest = new Promise((resolve, reject) => {
        const image = new Image();
        let finished = false;
        let timeoutId = 0;
        entry.image = image;

        const cleanup = () => {
          global.clearTimeout(timeoutId);
          image.onload = null;
          image.onerror = null;
        };
        const fail = (error) => {
          if (finished) return;
          finished = true;
          entry.settled = true;
          cleanup();
          reject(error);
        };
        const succeed = () => {
          if (finished) return;
          finished = true;
          entry.settled = true;
          cleanup();
          resolve(image);
        };

        entry.cancel = () => {
          if (finished) return;
          try {
            image.removeAttribute('src');
          } catch (_) {
            // Best-effort cancellation for older WebKit.
          }
          fail(new AssetRequestCancelled());
        };
        image.alt = '';
        image.decoding = 'async';
        // Detached Image objects marked `lazy` can be deferred forever because
        // they have no viewport box. The app already defers every non-shell
        // request until the active shell succeeds, so only active layers load.
        image.loading = 'eager';
        image.fetchPriority = asset.slot === 'shell' ? 'high' : 'auto';
        image.draggable = false;
        image.onload = () => {
          const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
          Promise.resolve(decoded).catch(() => {}).then(succeed);
        };
        image.onerror = () => fail(new Error(`Could not load ${asset.description}.`));
        timeoutId = global.setTimeout(() => {
          try {
            image.removeAttribute('src');
          } catch (_) {
            // Best-effort abort before exposing a retryable timeout.
          }
          fail(new Error(`Timed out loading ${asset.description}.`));
        }, this.loadTimeoutMs);
        image.src = url;
      });

      // The eight-image LRU holds completed loads only. Mixed D-pad pairs can
      // require more than eight layers at once; evicting an in-flight layer
      // would cancel the render that requested it.
      entry.promise = rawRequest.then((image) => {
        if (!this.disposed) {
          this.cache.set(url, entry);
          this.evictOverflow();
        }
        return image;
      }).finally(() => {
        if (this.pendingLoads.get(url) === entry) this.pendingLoads.delete(url);
      });
      this.pendingLoads.set(url, entry);
      return entry.promise;
    }

    async render(state) {
      if (this.disposed) throw new StalePreviewRequest();
      const requestToken = this.sequence.next();
      const resolved = core.resolveLayers(state);
      const shellImage = await this.loadAsset(resolved.layers[0]);
      if (this.disposed || !this.sequence.isCurrent(requestToken)) throw new StalePreviewRequest();
      const remaining = await Promise.all(resolved.layers.slice(1).map((asset) => this.loadAsset(asset)));
      const preloaded = [shellImage, ...remaining];
      if (this.disposed || !this.sequence.isCurrent(requestToken)) throw new StalePreviewRequest();

      const fragment = document.createDocumentFragment();
      resolved.layers.forEach((asset, index) => {
        const image = registeredImage(asset, preloaded[index]);
        image.setAttribute('class', 'pfg-sw1-builder-v3__layer');
        image.dataset.pfgV3Layer = asset.id;
        image.dataset.pfgV3Slot = asset.slot;
        if (asset.clipSide) image.dataset.pfgV3Side = asset.clipSide;
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        image.setAttribute('width', manifest.coordinateSpace.width);
        image.setAttribute('height', manifest.coordinateSpace.height);
        image.loading = 'eager';
        image.fetchPriority = index === 0 ? 'high' : 'auto';
        image.draggable = false;
        fragment.append(image);
      });

      if (this.disposed || !this.sequence.isCurrent(requestToken)) throw new StalePreviewRequest();
      this.stack.replaceChildren(fragment);
      this.stack.dataset.pfgV3Composition = resolved.layers.map((asset) => asset.id).join('|');
      return resolved;
    }

    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.sequence.invalidate();
      this.cache.forEach((entry) => {
        if (!entry.settled) entry.cancel();
      });
      this.pendingLoads.forEach((entry) => entry.cancel());
      this.pendingLoads.clear();
      this.cache.clear();
      renderers.delete(this);
    }
  }

  class CustomArtworkEditor {
    static MAX_FILE_BYTES = 10 * 1024 * 1024;
    static MAX_TOTAL_BYTES = 19 * 1024 * 1024;

    static SHELL_BOXES = Object.freeze({
      left: Object.freeze({ centerX: 393, centerY: 799.5, width: 444, height: 1343 }),
      right: Object.freeze({ centerX: 1213, centerY: 799.5, width: 436, height: 1343 })
    });

    constructor({ root, stage, stack, assetMap, signal, onActivate, onChange }) {
      this.root = root;
      this.stage = stage;
      this.stack = stack;
      this.assetMap = assetMap;
      this.onActivate = onActivate;
      this.onChange = onChange;
      this.loading = false;
      this.uploadSequence = 0;
      this.signal = signal;
      this.active = false;
      this.slotIndex = 0;
      this.slots = Array.from({ length: 4 }, () => this.createSlot());
      this.state = null;
      this.pointer = null;

      this.field = root.querySelector('[data-pfg-v3-artwork-field]');
      this.input = root.querySelector('[data-pfg-v3-artwork-upload]');
      this.status = root.querySelector('[data-pfg-v3-artwork-status]');
      this.editor = root.querySelector('[data-pfg-v3-artwork-editor]');
      this.slotGroup = root.querySelector('[data-pfg-v3-artwork-slot]');
      this.placementGroup = root.querySelector('[data-pfg-v3-artwork-placement]');
      this.sideGroup = root.querySelector('[data-pfg-v3-artwork-side]');
      this.fitSelect = root.querySelector('[data-pfg-v3-artwork-fit]');
      this.xInput = root.querySelector('[data-pfg-v3-artwork-x]');
      this.yInput = root.querySelector('[data-pfg-v3-artwork-y]');
      this.scaleInput = root.querySelector('[data-pfg-v3-artwork-scale]');
      this.rotationInput = root.querySelector('[data-pfg-v3-artwork-rotation]');
      this.xOutput = root.querySelector('[data-pfg-v3-artwork-x-output]');
      this.yOutput = root.querySelector('[data-pfg-v3-artwork-y-output]');
      this.scaleOutput = root.querySelector('[data-pfg-v3-artwork-scale-output]');
      this.rotationOutput = root.querySelector('[data-pfg-v3-artwork-rotation-output]');
      this.useButton = root.querySelector('[data-pfg-v3-artwork-use]');
      this.copyButton = root.querySelector('[data-pfg-v3-artwork-copy]');
      this.resetButton = root.querySelector('[data-pfg-v3-artwork-reset]');
      this.removeButton = root.querySelector('[data-pfg-v3-artwork-remove]');
      this.removeAllButton = root.querySelector('[data-pfg-v3-artwork-remove-all]');
      this.bind();
      this.refreshPanel();
    }

    createSlot() {
      return {
        url: '',
        file: null,
        width: 0,
        height: 0,
        revokeUrl: false,
        fileName: '',
        placement: 'both',
        side: 'left',
        transforms: {
          left: this.defaultTransform(),
          right: this.defaultTransform()
        }
      };
    }

    get currentSlot() { return this.slots[this.slotIndex]; }
    get url() { return this.currentSlot.url; }
    set url(value) { this.currentSlot.url = value; }
    get revokeUrl() { return this.currentSlot.revokeUrl; }
    set revokeUrl(value) { this.currentSlot.revokeUrl = value; }
    get fileName() { return this.currentSlot.fileName; }
    set fileName(value) { this.currentSlot.fileName = value; }
    get placement() { return this.currentSlot.placement; }
    set placement(value) { this.currentSlot.placement = value; }
    get side() { return this.currentSlot.side; }
    set side(value) { this.currentSlot.side = value; }
    get transforms() { return this.currentSlot.transforms; }

    uploadCount() {
      return this.slots.filter((slot) => Boolean(slot.url)).length;
    }

    defaultTransform() {
      return { x: 0, y: 0, scale: 1, rotation: 0, fit: 'cover' };
    }

    clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, Number(value) || 0));
    }

    bind() {
      const options = { signal: this.signal };
      this.input.addEventListener('change', () => this.handleFiles(Array.from(this.input.files || [])), options);
      this.slotGroup.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button || !this.slotGroup.contains(button)) return;
        this.setSlot(Number(button.dataset.value));
      }, options);
      this.placementGroup.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button || !this.placementGroup.contains(button)) return;
        this.setPlacement(button.dataset.value);
      }, options);
      this.sideGroup.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button || !this.sideGroup.contains(button)) return;
        this.setSide(button.dataset.value);
      }, options);
      this.fitSelect.addEventListener('change', () => {
        this.transforms[this.side].fit = this.fitSelect.value === 'contain' ? 'contain' : 'cover';
        this.sync(this.state);
      }, options);
      [this.xInput, this.yInput, this.scaleInput, this.rotationInput].forEach((input) => {
        input.addEventListener('input', () => this.updateFromInputs(), options);
      });
      this.useButton.addEventListener('click', () => this.activate(), options);
      this.copyButton.addEventListener('click', () => this.copyPlacement(), options);
      this.resetButton.addEventListener('click', () => this.resetPlacement(), options);
      this.removeButton.addEventListener('click', () => this.removeUpload(), options);
      this.removeAllButton.addEventListener('click', () => this.removeAllUploads(), options);
      this.stage.addEventListener('pointerdown', (event) => this.beginDrag(event), options);
      this.stage.addEventListener('pointermove', (event) => this.drag(event), options);
      this.stage.addEventListener('pointerup', (event) => this.endDrag(event), options);
      this.stage.addEventListener('pointercancel', (event) => this.endDrag(event), options);
    }

    async handleFiles(fileList) {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      if (this.loading) {
        this.status.textContent = 'Please wait for your selected images to finish loading.';
        return;
      }
      const emptySlots = this.slots.map((slot, index) => slot.url ? -1 : index).filter(index => index >= 0);
      let targetSlots = emptySlots.slice(0, files.length);
      if (!emptySlots.length && files.length === 1) targetSlots = [this.slotIndex];
      const prepared = [];
      const sequence = ++this.uploadSequence;
      this.loading = true;
      this.input.disabled = true;
      this.onChange?.();
      try {
        if (files.length > 4 || targetSlots.length < files.length) throw new Error('Choose up to four designs. Remove an existing design to make room, or choose one file to replace the selected full slot.');
        const retainedBytes = this.slots.reduce((total, slot, index) => total + (targetSlots.includes(index) ? 0 : slot.file?.size || 0), 0);
        if (retainedBytes + files.reduce((total, file) => total + file.size, 0) > CustomArtworkEditor.MAX_TOTAL_BYTES) throw new Error('Your artwork files must total 19 MB or less.');
        for (const file of files) {
          if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error(`${file.name} is not a PNG, JPG or WebP image.`);
          if (!file.size) throw new Error(`${file.name} is empty.`);
          if (file.size > CustomArtworkEditor.MAX_FILE_BYTES) throw new Error(`${file.name} is larger than 10 MB.`);
          const candidate = {file, url: global.URL.createObjectURL(file)};
          prepared.push(candidate);
          await new Promise((resolve, reject) => {
            const probe = new Image();
            const timer = global.setTimeout(() => { probe.onload = probe.onerror = null; reject(new Error(`${file.name} took too long to load. Please try again.`)); }, IMAGE_LOAD_TIMEOUT_MS);
            probe.onload = () => { global.clearTimeout(timer); candidate.width = probe.naturalWidth; candidate.height = probe.naturalHeight; resolve(); };
            probe.onerror = () => { global.clearTimeout(timer); reject(new Error(`${file.name} could not be read.`)); };
            probe.src = candidate.url;
          });
          if (this.signal.aborted || sequence !== this.uploadSequence) throw new Error('The image selection changed before it finished loading.');
        }
        if (this.signal.aborted || sequence !== this.uploadSequence) throw new Error('The image selection changed before it finished loading.');
        prepared.forEach((candidate, index) => {
          const targetIndex = targetSlots[index], previous = this.slots[targetIndex];
          if (previous.url && previous.revokeUrl) global.URL.revokeObjectURL(previous.url);
          this.slots[targetIndex] = {...this.createSlot(),url:candidate.url,revokeUrl:true,file:candidate.file,fileName:candidate.file.name,width:candidate.width,height:candidate.height};
        });
        prepared.length = 0;
        this.slotIndex = targetSlots[0];
        this.active = true;
        const count = this.uploadCount();
        this.status.textContent = `${count} design file${count === 1 ? '' : 's'} active. Editing Design ${this.slotIndex + 1}: ${this.fileName}.`;
        this.refreshPanel();
        await this.onActivate?.();
        this.sync(this.state);
      } catch (error) {
        prepared.forEach(candidate => global.URL.revokeObjectURL(candidate.url));
        if (!this.signal.aborted) this.status.textContent = error.message;
      } finally {
        this.loading = false;
        this.input.value = '';
        this.input.disabled = this.state?.angle === 'back';
        this.onChange?.();
      }
    }

    getArtworks() {
      if (!this.active || this.state?.design !== 'none') return [];
      return this.slots.flatMap((slot, index) => slot.url ? [{slot:index + 1,file:slot.file,view:'front',width:slot.width,height:slot.height,placement:slot.placement,transforms:{left:{...slot.transforms.left},right:{...slot.transforms.right}}}] : []);
    }

    async handleFile(file) {
      return this.handleFiles(file ? [file] : []);
    }

    async loadPreviewUrl(url, fileName = 'Customer artwork') {
      if (!url) throw new Error('Artwork URL is required.');
      await new Promise((resolve, reject) => {
        const probe = new Image();
        probe.onload = resolve;
        probe.onerror = () => reject(new Error('The artwork preview URL could not be read.'));
        probe.src = url;
      });
      if (this.url && this.revokeUrl) global.URL.revokeObjectURL(this.url);
      this.url = url;
      this.currentSlot.file = null;
      this.currentSlot.width = 0;
      this.currentSlot.height = 0;
      this.revokeUrl = false;
      this.fileName = fileName;
      this.active = true;
      this.transforms.left = this.defaultTransform();
      this.transforms.right = this.defaultTransform();
      this.status.textContent = `${fileName} is active. Drag the preview or use the precision controls.`;
      this.editor.hidden = false;
      this.refreshPanel();
      await this.onActivate?.();
      this.sync(this.state);
    }

    async activate() {
      if (!this.uploadCount()) return;
      this.active = true;
      const count = this.uploadCount();
      this.status.textContent = `${count} design file${count === 1 ? '' : 's'} active. Editing Design ${this.slotIndex + 1}: ${this.fileName}.`;
      this.refreshPanel();
      await this.onActivate?.();
      this.sync(this.state);
    }

    deactivateForPremade() {
      const count = this.uploadCount();
      if (!count) return;
      this.active = false;
      this.status.textContent = `${count} uploaded design${count === 1 ? '' : 's'} saved in this browser session; the premade design is active.`;
      this.refreshPanel();
      this.removeLayers();
      this.onChange?.();
    }

    removeUpload() {
      this.uploadSequence += 1;
      const removedIndex = this.slotIndex;
      const wasActive = this.active;
      if (this.url && this.revokeUrl) global.URL.revokeObjectURL(this.url);
      this.slots[removedIndex] = this.createSlot();
      this.input.value = '';
      const nextIndex = this.slots.findIndex((slot) => Boolean(slot.url));
      if (nextIndex >= 0) {
        this.slotIndex = nextIndex;
        this.active = wasActive;
        const count = this.uploadCount();
        this.status.textContent = `Removed Design ${removedIndex + 1}. ${count} design file${count === 1 ? '' : 's'} remain${count === 1 ? 's' : ''}; editing Design ${nextIndex + 1}: ${this.fileName}.`;
      } else {
        this.slotIndex = 0;
        this.active = false;
        this.status.textContent = 'No customer artwork selected.';
        this.editor.hidden = true;
      }
      this.refreshPanel();
      this.sync(this.state);
    }

    removeAllUploads() {
      this.uploadSequence += 1;
      this.slots.forEach((slot) => {
        if (slot.url && slot.revokeUrl) global.URL.revokeObjectURL(slot.url);
      });
      this.slots = Array.from({ length: 4 }, () => this.createSlot());
      this.slotIndex = 0;
      this.active = false;
      this.input.value = '';
      this.status.textContent = 'No customer artwork selected.';
      this.editor.hidden = true;
      this.refreshPanel();
      this.removeLayers();
      this.onChange?.();
    }

    setSlot(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slots.length || !this.slots[index].url) return;
      this.slotIndex = index;
      this.status.textContent = `Editing Design ${index + 1}: ${this.fileName}.`;
      this.refreshPanel();
    }

    setSide(side) {
      if (!['left', 'right'].includes(side)) return;
      if (this.placement !== 'both' && side !== this.placement) return;
      this.side = side;
      this.refreshPanel();
    }

    setPlacement(placement) {
      if (!['left', 'right', 'both'].includes(placement)) return;
      this.placement = placement;
      if (placement !== 'both') this.side = placement;
      const label = placement === 'both' ? 'both Joy-Cons' : `the ${placement} Joy-Con`;
      this.status.textContent = `${this.fileName || 'Customer artwork'} will appear on ${label}.`;
      this.refreshPanel();
      this.sync(this.state);
    }

    updateFromInputs() {
      const transform = this.transforms[this.side];
      transform.x = this.clamp(this.xInput.value, -500, 500);
      transform.y = this.clamp(this.yInput.value, -500, 500);
      transform.scale = this.clamp(this.scaleInput.value, 25, 400) / 100;
      transform.rotation = this.clamp(this.rotationInput.value, -180, 180);
      transform.fit = this.fitSelect.value === 'contain' ? 'contain' : 'cover';
      this.refreshPanel();
      this.sync(this.state);
    }

    copyPlacement() {
      const other = this.side === 'left' ? 'right' : 'left';
      this.transforms[other] = { ...this.transforms[this.side] };
      this.status.textContent = `Copied ${this.side} placement to the ${other} Joy-Con.`;
      this.sync(this.state);
    }

    resetPlacement() {
      this.transforms[this.side] = this.defaultTransform();
      this.status.textContent = `Reset ${this.side} Joy-Con artwork placement.`;
      this.refreshPanel();
      this.sync(this.state);
    }

    beginDrag(event) {
      if (!this.active || !this.url || this.state?.angle !== 'front' || event.button !== 0) return;
      const rect = this.stage.getBoundingClientRect();
      if (this.placement === 'both') this.setSide(event.clientX < rect.left + (rect.width / 2) ? 'left' : 'right');
      else this.setSide(this.placement);
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.stage.setPointerCapture?.(event.pointerId);
      this.stage.classList.add('is-artwork-dragging');
      event.preventDefault();
    }

    drag(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const rect = this.stage.getBoundingClientRect();
      const transform = this.transforms[this.side];
      transform.x = this.clamp(transform.x + ((event.clientX - this.pointer.x) / rect.width * 1600), -500, 500);
      transform.y = this.clamp(transform.y + ((event.clientY - this.pointer.y) / rect.height * 1600), -500, 500);
      this.pointer.x = event.clientX;
      this.pointer.y = event.clientY;
      this.refreshPanel();
      this.sync(this.state);
      event.preventDefault();
    }

    endDrag(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.stage.releasePointerCapture?.(event.pointerId);
      this.pointer = null;
      this.stage.classList.remove('is-artwork-dragging');
    }

    removeLayers() {
      this.stack.querySelectorAll('[data-pfg-v3-custom-artwork]').forEach((node) => node.remove());
    }

    maskUrl(state, side) {
      const asset = core.resolveArtworkMask(state, side);
      return this.assetMap[asset.id] || this.assetMap[asset.file] || '';
    }

    installLayer(state, side, anchor, slot, slotIndex) {
      const maskUrl = this.maskUrl(state, side);
      if (!maskUrl) return;
      const box = core.resolveGeometry(state)[side]?.shellBox || CustomArtworkEditor.SHELL_BOXES[side];
      const transform = slot.transforms[side];
      const layer = document.createElement('div');
      layer.className = 'pfg-sw1-builder-v3__custom-artwork';
      layer.dataset.pfgV3CustomArtwork = `${slotIndex}-${side}`;
      layer.dataset.pfgV3UploadSlot = String(slotIndex);
      layer.dataset.pfgV3Slot = 'design';
      layer.dataset.pfgV3Side = side;
      layer.setAttribute('aria-hidden', 'true');
      layer.style.setProperty('--pfg-v3-artwork-mask', `url("${maskUrl}")`);

      const image = document.createElement('img');
      image.className = 'pfg-sw1-builder-v3__custom-artwork-image';
      image.src = slot.url;
      image.alt = '';
      image.draggable = false;
      image.style.left = `${((box.centerX + transform.x) / 1600) * 100}%`;
      image.style.top = `${((box.centerY + transform.y) / 1600) * 100}%`;
      image.style.width = `${((box.width * transform.scale) / 1600) * 100}%`;
      image.style.height = `${((box.height * transform.scale) / 1600) * 100}%`;
      image.style.objectFit = transform.fit;
      image.style.setProperty('--pfg-v3-artwork-rotation', `${transform.rotation}deg`);
      layer.append(image);
      this.stack.insertBefore(layer, anchor);
    }

    sync(state) {
      if (!state) return;
      this.state = { ...state };
      this.onChange?.();
      this.removeLayers();
      const editable = this.active && this.uploadCount() > 0 && state.design === 'none' && state.angle === 'front';
      this.stage.classList.toggle('is-artwork-editing', editable);
      this.field.classList.toggle('is-view-disabled', state.angle === 'back');
      this.input.disabled = this.loading || state.angle === 'back';
      this.editor.querySelectorAll('button, input, select').forEach((control) => {
        control.disabled = state.angle === 'back';
      });
      if (!editable) {
        this.refreshPanel();
        return;
      }
      const anchor = Array.from(this.stack.children).find((node) => node.dataset.pfgV3Slot?.startsWith('controls')) || null;
      this.slots.forEach((slot, slotIndex) => {
        if (!slot.url) return;
        if (slot.placement === 'both' || slot.placement === 'left') this.installLayer(state, 'left', anchor, slot, slotIndex);
        if (slot.placement === 'both' || slot.placement === 'right') this.installLayer(state, 'right', anchor, slot, slotIndex);
      });
      this.refreshPanel();
    }

    refreshPanel() {
      const hasUploads = this.uploadCount() > 0;
      const transform = this.transforms[this.side];
      this.editor.hidden = !hasUploads;
      setPressed(this.slotGroup, String(this.slotIndex));
      this.slotGroup.querySelectorAll('button[data-value]').forEach((button) => {
        const index = Number(button.dataset.value);
        const slot = this.slots[index];
        button.textContent = slot.fileName ? `Design ${index + 1} · Added` : `Design ${index + 1}`;
        button.title = slot.fileName || `Design ${index + 1} is empty`;
        button.disabled = this.state?.angle === 'back' || !slot.url;
      });
      setPressed(this.placementGroup, this.placement);
      setPressed(this.sideGroup, this.side);
      this.sideGroup.querySelectorAll('button[data-value]').forEach((button) => {
        button.disabled = this.state?.angle === 'back' || (this.placement !== 'both' && button.dataset.value !== this.placement);
      });
      this.xInput.value = String(Math.round(transform.x));
      this.yInput.value = String(Math.round(transform.y));
      this.scaleInput.value = String(Math.round(transform.scale * 100));
      this.rotationInput.value = String(Math.round(transform.rotation));
      this.fitSelect.value = transform.fit;
      this.xOutput.textContent = `${Math.round(transform.x)} px`;
      this.yOutput.textContent = `${Math.round(transform.y)} px`;
      this.scaleOutput.textContent = `${Math.round(transform.scale * 100)}%`;
      this.rotationOutput.textContent = `${Math.round(transform.rotation)}°`;
      this.useButton.hidden = !hasUploads || this.active;
      this.stage.classList.toggle('is-artwork-editing', this.active && hasUploads && this.state?.angle === 'front');
    }

    isActive() {
      return this.active && this.uploadCount() > 0;
    }

    dispose() {
      this.uploadSequence += 1;
      this.removeLayers();
      this.stage.classList.remove('is-artwork-editing', 'is-artwork-dragging');
      this.slots.forEach((slot) => {
        if (slot.url && slot.revokeUrl) global.URL.revokeObjectURL(slot.url);
      });
      this.slots = Array.from({ length: 4 }, () => this.createSlot());
    }
  }

  function parseJson(root, selector, fallback = {}) {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(`PFG SW1 Builder V3: invalid JSON in ${selector}.`, error);
      return fallback;
    }
  }

  function isPlainRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function populateSelect(select, items, selectedKey) {
    if (!select) return '';
    const fragment = document.createDocumentFragment();
    alphabeticalOptions(items).forEach((item) => {
      const option = document.createElement('option');
      option.value = item.key;
      option.textContent = item.name;
      fragment.append(option);
    });
    select.replaceChildren(fragment);
    select.value = items.some((item) => item.key === selectedKey) ? selectedKey : items[0]?.key || '';
    return select.value;
  }

  function alphabeticalOptions(items) {
    const neutralKeys = new Set(['none', 'matching']);
    return [...items].sort((left, right) => {
      const leftNeutral = neutralKeys.has(left.key);
      const rightNeutral = neutralKeys.has(right.key);
      if (leftNeutral !== rightNeutral) return leftNeutral ? -1 : 1;
      return left.name.localeCompare(right.name, 'en', { sensitivity: 'base', numeric: true });
    });
  }

  function setPressed(container, selectedKey) {
    const buttons = Array.from(container?.querySelectorAll('button[data-value]') || []);
    buttons.forEach((button) => {
      const selected = button.dataset.value === selectedKey;
      button.setAttribute('aria-pressed', String(selected));
      button.tabIndex = selected && !button.disabled ? 0 : -1;
    });
    const firstEnabled = buttons.find((button) => !button.disabled);
    if (firstEnabled && !buttons.some((button) => button.tabIndex === 0)) firstEnabled.tabIndex = 0;
  }

  function setGroupDisabled(container, disabled, explanation) {
    if (!container) return;
    container.classList.toggle('is-view-disabled', disabled);
    container.setAttribute('aria-disabled', String(disabled));
    Array.from(container.querySelectorAll('button[data-value]')).forEach((button) => {
      button.disabled = disabled;
      button.title = disabled ? explanation : '';
      if (disabled) button.tabIndex = -1;
    });
  }

  function installRovingKeyboard(container, signal) {
    if (!container) return;
    container.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const buttons = Array.from(container.querySelectorAll('button[data-value]:not(:disabled)'));
      const currentIndex = buttons.indexOf(document.activeElement);
      if (!buttons.length || currentIndex < 0) return;
      event.preventDefault();
      const backwards = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      let nextIndex = backwards ? currentIndex - 1 : currentIndex + 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = buttons.length - 1;
      nextIndex = (nextIndex + buttons.length) % buttons.length;
      buttons[nextIndex].focus();
      buttons[nextIndex].click();
    }, { signal });
  }

  function safeReplaceUrlState(state) {
    if (!global.history?.replaceState || !global.URL) return true;
    try {
      const url = new URL(global.location.href);
      url.search = core.stateToSearchParams(state, url.searchParams).toString();
      global.history.replaceState(global.history.state, '', url);
      return true;
    } catch (error) {
      console.warn('PFG SW1 Builder V3: the preview loaded, but URL state could not be updated.', error);
      return false;
    }
  }

  function assetMapErrors(assetMap) {
    if (!isPlainRecord(assetMap) || !isPlainRecord(manifest.assets)) return ['invalid asset URL map or manifest asset collection'];
    return Object.values(manifest.assets).filter((asset) => !(assetMap[asset.id] || assetMap[asset.file])).map((asset) => asset.id);
  }

  function mount(root) {
    if (!root || moduleDisposed || instances.has(root)) return instances.get(root) || null;

    let contract;
    try {
      contract = core.validateDataContract();
    } catch (error) {
      settleRootFailure(root, 'Preview data is malformed. Checkout remains disabled.');
      console.error('PFG SW1 Builder V3 data-contract validation threw:', error);
      return null;
    }
    if (!contract.ok) {
      settleRootFailure(root, 'Preview data is incomplete. This draft has not been enabled for checkout.');
      console.error('PFG SW1 Builder V3 data-contract errors:', contract.errors);
      return null;
    }

    Array.from(instances.values()).forEach((instance) => {
      if (!instance.root.isConnected) instance.destroy();
    });
    // V3 intentionally permits one independently stateful root per page. A
    // second root would fight the shared address-bar state, so fail it closed.
    if (instances.size > 0) {
      settleRootFailure(root, 'Only one Switch 1 preview can run on a page. Checkout remains disabled.');
      return null;
    }

    const requiredSelectors = [
      '[data-pfg-v3-stage]', '[data-pfg-v3-stack]', '[data-pfg-v3-fallback]', '[data-pfg-v3-loading]',
      '[data-pfg-v3-error]', '[data-pfg-v3-status]', '[data-pfg-v3-shell]', '[data-pfg-v3-left-shell]', '[data-pfg-v3-right-shell]', '[data-pfg-v3-buttons]',
      '[data-pfg-v3-left-buttons]', '[data-pfg-v3-right-buttons]', '[data-pfg-v3-accent]',
      '[data-pfg-v3-design]', '[data-pfg-v3-caps]', '[data-pfg-v3-summary]', '[data-pfg-v3-form]',
      '[data-pfg-v3-artwork-field]', '[data-pfg-v3-artwork-upload]', '[data-pfg-v3-artwork-status]', '[data-pfg-v3-artwork-editor]',
      '[data-pfg-v3-artwork-slot]', '[data-pfg-v3-artwork-placement]', '[data-pfg-v3-artwork-side]', '[data-pfg-v3-artwork-fit]', '[data-pfg-v3-artwork-x]', '[data-pfg-v3-artwork-y]',
      '[data-pfg-v3-artwork-scale]', '[data-pfg-v3-artwork-rotation]', '[data-pfg-v3-artwork-remove-all]'
    ];
    const missingMarkup = requiredSelectors.filter((selector) => !root.querySelector(selector));
    if (missingMarkup.length) {
      settleRootFailure(root, 'Preview markup is incomplete. Checkout remains disabled.');
      console.error('PFG SW1 Builder V3 missing markup:', missingMarkup);
      return null;
    }

    const abort = new AbortController();
    const { signal } = abort;
    const assetMap = parseJson(root, '[data-pfg-sw1-builder-v3-assets]', null);
    if (!isPlainRecord(assetMap)) {
      settleRootFailure(root, 'Preview asset data is malformed. Checkout remains disabled.');
      console.error('PFG SW1 Builder V3 asset URL map must be a JSON object.');
      return null;
    }
    const missingAssetUrls = assetMapErrors(assetMap);
    if (missingAssetUrls.length) {
      settleRootFailure(root, 'Preview asset URLs are incomplete. Checkout remains disabled.');
      console.error('PFG SW1 Builder V3 missing asset URLs:', missingAssetUrls);
      return null;
    }
    const assetMapSignature = stableSignature(Object.keys(manifest.assets).sort().flatMap((id) => [id, assetMap[id] || assetMap[manifest.assets[id].file]]));

    const parsedDefaults = parseJson(root, '[data-pfg-sw1-builder-v3-defaults]', catalog.DEFAULT_STATE);
    if (!isPlainRecord(parsedDefaults)) {
      settleRootFailure(root, 'Preview defaults are malformed. Checkout remains disabled.');
      console.error('PFG SW1 Builder V3 defaults must be a JSON object.');
      return null;
    }
    const defaults = core.normalizeState({ ...catalog.DEFAULT_STATE, ...parsedDefaults });
    const stack = root.querySelector('[data-pfg-v3-stack]');
    let committedState = defaults;
    if (stack.childElementCount && root.dataset.pfgV3CommittedState) {
      try {
        committedState = core.normalizeState(JSON.parse(root.dataset.pfgV3CommittedState));
      } catch (_) {
        committedState = defaults;
      }
    }
    let desiredState = committedState;
    let renderToken = 0;
    let destroyed = false;

    const stage = root.querySelector('[data-pfg-v3-stage]');
    const fallback = root.querySelector('[data-pfg-v3-fallback]');
    const loading = root.querySelector('[data-pfg-v3-loading]');
    const errorBox = root.querySelector('[data-pfg-v3-error]');
    const shellSelect = root.querySelector('[data-pfg-v3-shell]');
    const leftShellSelect = root.querySelector('[data-pfg-v3-left-shell]');
    const rightShellSelect = root.querySelector('[data-pfg-v3-right-shell]');
    const buttonSelect = root.querySelector('[data-pfg-v3-buttons]');
    const leftButtonSelect = root.querySelector('[data-pfg-v3-left-buttons]');
    const rightButtonSelect = root.querySelector('[data-pfg-v3-right-buttons]');
    const designSelect = root.querySelector('[data-pfg-v3-design]');
    const accentSelect = root.querySelector('[data-pfg-v3-accent]');
    const capContainer = root.querySelector('[data-pfg-v3-caps]');
    const shellSwatch = root.querySelector('[data-pfg-v3-shell-swatch]');
    const leftShellSwatch = root.querySelector('[data-pfg-v3-left-shell-swatch]');
    const rightShellSwatch = root.querySelector('[data-pfg-v3-right-shell-swatch]');
    const matchingShellField = root.querySelector('[data-pfg-v3-matching-shell-field]');
    const independentShellFields = root.querySelector('[data-pfg-v3-independent-shell-fields]');
    const matchingButtonsField = root.querySelector('[data-pfg-v3-matching-buttons-field]');
    const independentButtonsFields = root.querySelector('[data-pfg-v3-independent-buttons-fields]');
    const summary = root.querySelector('[data-pfg-v3-summary]');
    const materialBadge = root.querySelector('[data-pfg-v3-material-badge]');
    const angleBadge = root.querySelector('[data-pfg-v3-angle-badge]');
    const accentNote = root.querySelector('[data-pfg-v3-accent-note]');
    const rearNote = root.querySelector('[data-pfg-v3-rear-note]');
    const angleNote = root.querySelector('[data-pfg-v3-angle-note]');
    const contextWarning = root.querySelector('[data-pfg-v3-context-warning]');
    const limitations = root.querySelector('[data-pfg-v3-limitations]');
    const status = root.querySelector('[data-pfg-v3-status]');
    const form = root.querySelector('[data-pfg-v3-form]');
    const renderer = new LayerStackRenderer(stack, assetMap);
    let checkout = null;
    const artworkEditor = new CustomArtworkEditor({
      root,
      stage,
      stack,
      assetMap,
      signal,
      onActivate: () => commit({ ...desiredState, design: 'none', angle: 'front' }),
      onChange: () => checkout?.refresh()
    });

    function updateStickyOffset() {
      const candidates = Array.from(document.querySelectorAll('sticky-header, .shopify-section-header-sticky, [data-sticky-header]'))
        .filter((node) => !root.contains(node));
      const bottom = candidates.reduce((maximum, node) => {
        const style = global.getComputedStyle(node);
        if (!['fixed', 'sticky'].includes(style.position)) return maximum;
        const rect = node.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top > 1) return maximum;
        return Math.max(maximum, rect.bottom);
      }, 0);
      root.style.setProperty('--pfg-v3-mobile-sticky-top', `${Math.max(8, Math.ceil(bottom + 8))}px`);
    }
    let stickyOffsetFrame = 0;
    const scheduleStickyOffset = () => {
      if (stickyOffsetFrame) return;
      stickyOffsetFrame = global.requestAnimationFrame(() => {
        stickyOffsetFrame = 0;
        updateStickyOffset();
      });
    };
    updateStickyOffset();
    global.addEventListener('resize', scheduleStickyOffset, { passive: true, signal });
    global.addEventListener('scroll', scheduleStickyOffset, { passive: true, signal });

    function updateCopy(state) {
      const shell = catalog.getShell(state.shell);
      const rightShell = catalog.getShell(state.rightShell);
      summary.textContent = artworkEditor.isActive()
        ? `${core.describeState(state)} Custom uploaded artwork is active on the front preview.`
        : core.describeState(state);
      if (shellSwatch) {
        shellSwatch.style.setProperty('--pfg-v3-swatch', shell.swatch);
        shellSwatch.style.setProperty('--pfg-v3-swatch-secondary', shell.secondarySwatch || shell.swatch);
        shellSwatch.classList.toggle('is-transparent', shell.family === 'transparent');
      }
      if (leftShellSwatch) {
        leftShellSwatch.style.setProperty('--pfg-v3-swatch', shell.swatch);
        leftShellSwatch.style.setProperty('--pfg-v3-swatch-secondary', shell.secondarySwatch || shell.swatch);
        leftShellSwatch.classList.toggle('is-transparent', shell.family === 'transparent');
      }
      if (rightShellSwatch) {
        rightShellSwatch.style.setProperty('--pfg-v3-swatch', rightShell.swatch);
        rightShellSwatch.style.setProperty('--pfg-v3-swatch-secondary', rightShell.secondarySwatch || rightShell.swatch);
        rightShellSwatch.classList.toggle('is-transparent', rightShell.family === 'transparent');
      }
      if (materialBadge) materialBadge.textContent = shell.family !== rightShell.family
        ? 'Mixed shell finishes'
        : shell.family === 'transparent' ? 'Transparent shells' : 'Solid shells';
      if (angleBadge) angleBadge.textContent = state.angle === 'back' ? 'Rear view' : 'Front view';
      if (rearNote) rearNote.hidden = state.angle !== 'back';
      if (accentNote) {
        const kit = catalog.getAccent(state.accent);
        const buttonName = (key, isAbxy = false) => key === 'black-oem'
          ? isAbxy ? 'Black OEM with white-painted letters' : 'Black'
          : catalog.getButtonSet(key).name;
        const dpadNote = `The D-pad keeps ${buttonName(state.buttonColor)} from the left button option.`;
        if (kit.kind === 'jelly') {
          const rightColor = kit.partColors.X;
          const leftColor = kit.partColors.minus;
          const leftParts = state.layout === 'd-pad' ? '− and Capture' : 'four arrows, − and Capture';
          accentNote.textContent = `${rightColor} ABXY, + and Home; ${leftColor} ${leftParts}. Every kit button has a colored inner face and clear surround. ${state.layout === 'd-pad' ? `${dpadNote} ` : ''}Bumpers, triggers and SL/SR keep their selected button colors.`;
        } else if (kit.kind === 'colorful') {
          const abxy = `ABXY: X ${kit.partColors.X}, Y ${kit.partColors.Y}, A ${kit.partColors.A}, B ${kit.partColors.B}.`;
          const directions = state.layout === 'd-pad' ? dpadNote
            : `Arrows: Up ${kit.partColors.up}, Left ${kit.partColors.left}, Right ${kit.partColors.right}, Down ${kit.partColors.down}.`;
          accentNote.textContent = `${abxy} ${directions} +, −, Home, Capture, bumpers, triggers and SL/SR keep their selected button colors.`;
        } else {
          const abxy = buttonName(state.rightButtonColor, true);
          const directions = buttonName(state.buttonColor);
          accentNote.textContent = state.layout === 'd-pad'
            ? `ABXY: ${abxy}. D-pad: ${directions}, following the left button option. All other controls follow the button option selected for each Joy-Con.`
            : `ABXY: ${abxy}. Four arrow buttons: ${directions}. All other controls follow the button option selected for each Joy-Con.`;
        }
      }
      const warnings = core.reviewWarnings(state);
      if (contextWarning) {
        contextWarning.textContent = warnings.join(' ');
        contextWarning.hidden = warnings.length === 0;
      }
      stage.setAttribute('aria-label', core.accessibilityLabel(state));
    }

    function refreshControls(state) {
      const normalized = core.normalizeState(state);
      const shellChoices = core.availableShells(normalized).map((shell) => shell.views.includes(normalized.angle)
        ? shell
        : { ...shell, name: `${shell.name} (switches to Front)` });
      const rightShellChoices = core.availableRightShells(normalized).map((shell) => shell.views.includes(normalized.angle)
        ? shell
        : { ...shell, name: `${shell.name} (switches to Front)` });
      populateSelect(shellSelect, shellChoices, normalized.shell);
      populateSelect(leftShellSelect, shellChoices, normalized.shell);
      populateSelect(rightShellSelect, rightShellChoices, normalized.rightShell);
      if (matchingShellField) matchingShellField.hidden = normalized.pairMode !== 'matching';
      if (independentShellFields) independentShellFields.hidden = normalized.pairMode !== 'independent';
      const buttonChoices = core.availableButtonSets(normalized);
      populateSelect(buttonSelect, buttonChoices, normalized.buttonColor);
      populateSelect(leftButtonSelect, buttonChoices, normalized.buttonColor);
      populateSelect(rightButtonSelect, core.availableRightButtonSets(normalized), normalized.rightButtonColor);
      if (matchingButtonsField) matchingButtonsField.hidden = normalized.buttonPairMode !== 'matching';
      if (independentButtonsFields) independentButtonsFields.hidden = normalized.buttonPairMode !== 'independent';
      populateSelect(designSelect, core.availableDesigns(normalized), normalized.design);
      populateSelect(accentSelect, catalog.ACCENTS, normalized.accent);
      const rear = normalized.angle === 'back';
      const designsUnavailable = !core.availableDesigns(normalized).some((design) => design.key !== 'none');
      const rearExplanation = 'This option affects only the front preview. Return to Front to change it.';
      setGroupDisabled(root.querySelector('[data-pfg-v3-layout]'), rear, rearExplanation);
      setGroupDisabled(capContainer, rear, rearExplanation);
      designSelect.disabled = rear || designsUnavailable;
      if (rear) designSelect.value = 'none';
      designSelect.title = rear
        ? 'The selected premade is shown on the rear; change premades or edit customer uploads from the Front view.'
        : designsUnavailable ? 'No registered premade artwork is available for this shell family.' : '';
      designSelect.closest('.pfg-sw1-builder-v3__field')?.classList.toggle('is-view-disabled', rear || designsUnavailable);
      accentSelect.disabled = rear;
      accentSelect.title = rear ? rearExplanation : '';
      accentSelect.closest('.pfg-sw1-builder-v3__field')?.classList.toggle('is-view-disabled', rear);
      setPressed(root.querySelector('[data-pfg-v3-layout]'), normalized.layout);
      setPressed(root.querySelector('[data-pfg-v3-family]'), normalized.family);
      setPressed(root.querySelector('[data-pfg-v3-pair-mode]'), normalized.pairMode);
      setPressed(root.querySelector('[data-pfg-v3-button-pair-mode]'), normalized.buttonPairMode);
      setPressed(root.querySelector('[data-pfg-v3-angle]'), normalized.angle);
      setPressed(capContainer, normalized.capColor);
      const backButton = root.querySelector('[data-pfg-v3-angle] button[data-value="back"]');
      const rearSupported = catalog.getShell(normalized.shell).views.includes('back') && catalog.getShell(normalized.rightShell).views.includes('back');
      if (backButton) {
        backButton.disabled = !rearSupported;
        backButton.title = rearSupported ? '' : 'Rear preview is unavailable for this shell because its rail geometry is not registered.';
        if (!rearSupported) backButton.tabIndex = -1;
      }
      if (angleNote) {
        angleNote.hidden = rearSupported;
        angleNote.textContent = rearSupported ? '' : 'A rear preview is unavailable for one of your selected shell finishes. Your selected parts are preserved.';
      }
      setPressed(root.querySelector('[data-pfg-v3-angle]'), normalized.angle);
    }

    async function requestPreview(nextState, options = {}) {
      const { writeUrl = true, rollbackUrlOnFailure = false } = options;
      const requestedState = core.normalizeState(nextState);
      desiredState = requestedState;
      const token = ++renderToken;

      // Visible controls, copy, URL and labels describe only the last rendered
      // composition. A failed pending request therefore cannot split the UI.
      refreshControls(committedState);
      stage.setAttribute('aria-busy', 'true');
      checkout?.refresh();
      loading.hidden = false;
      errorBox.hidden = true;
      status.textContent = 'Loading preview.';

      try {
        const result = await renderer.render(requestedState);
        if (destroyed || token !== renderToken || !root.isConnected) return false;
        if (result.state.design !== 'none') artworkEditor.deactivateForPremade();
        artworkEditor.sync(result.state);
        committedState = { ...result.state };
        desiredState = committedState;
        root.dataset.pfgV3CommittedState = JSON.stringify(committedState);
        root.dataset.pfgV3ContractSignature = contractSignature;
        root.dataset.pfgV3AssetSignature = assetMapSignature;
        refreshControls(committedState);
        updateCopy(committedState);
        const urlSynced = writeUrl ? safeReplaceUrlState(committedState) : true;
        fallback.hidden = true;
        stack.hidden = false;
        root.dataset.pfgV3Ready = 'true';
        root.dataset.pfgV3UrlSync = urlSynced ? 'true' : 'false';
        status.textContent = urlSynced
          ? `${core.accessibilityLabel(committedState)} loaded.`
          : `${core.accessibilityLabel(committedState)} loaded, but the address bar could not be updated.`;
        return true;
      } catch (error) {
        if (error instanceof StalePreviewRequest || destroyed || token !== renderToken) return false;
        desiredState = committedState;
        refreshControls(committedState);
        updateCopy(committedState);
        if (rollbackUrlOnFailure) safeReplaceUrlState(committedState);
        const hasCommittedStack = Boolean(stack.childElementCount);
        root.dataset.pfgV3Ready = hasCommittedStack ? 'true' : 'false';
        errorBox.textContent = `Preview unavailable: ${error.message}`;
        errorBox.hidden = false;
        if (hasCommittedStack) {
          stack.hidden = false;
          fallback.hidden = true;
          status.textContent = 'Preview could not be loaded. The previous valid preview remains visible.';
        } else {
          stack.hidden = true;
          revealStaticFallback(root);
          labelStaticFallback(root);
          status.textContent = 'Preview could not be loaded. A static fallback remains visible.';
        }
        return false;
      } finally {
        if (!destroyed && token === renderToken) {
          loading.hidden = true;
          stage.setAttribute('aria-busy', 'false');
          checkout?.refresh();
        }
      }
    }

    function commit(nextState, options) {
      return requestPreview(nextState, options);
    }

    function bindSegment(selector, stateKey, transform) {
      const container = root.querySelector(selector);
      container.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button || !container.contains(button) || button.disabled) return;
        const value = button.dataset.value;
        const nextState = transform ? transform(desiredState, value) : { ...desiredState, [stateKey]: value };
        commit(nextState);
      }, { signal });
      installRovingKeyboard(container, signal);
    }

    capContainer.replaceChildren();
    alphabeticalOptions(catalog.CAPS).forEach((cap) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.value = cap.key;
      button.className = 'pfg-sw1-builder-v3__cap-choice';
      button.style.setProperty('--pfg-v3-swatch', cap.swatch);
      button.setAttribute('aria-pressed', String(cap.key === committedState.capColor));
      const swatch = document.createElement('span');
      swatch.className = 'pfg-sw1-builder-v3__cap-swatch';
      swatch.setAttribute('aria-hidden', 'true');
      button.append(swatch, document.createTextNode(cap.name));
      capContainer.append(button);
    });
    if (limitations) {
      const list = document.createElement('ul');
      catalog.UNSUPPORTED_OPTIONS.forEach((message) => {
        const item = document.createElement('li');
        item.textContent = message;
        list.append(item);
      });
      limitations.replaceChildren(list);
    }

    bindSegment('[data-pfg-v3-layout]', 'layout', (current, value) => core.switchLayout(current, value));
    bindSegment('[data-pfg-v3-family]', 'family', (current, value) => core.switchFamily(current, value));
    bindSegment('[data-pfg-v3-pair-mode]', 'pairMode', (current, value) => ({
      ...current,
      pairMode: value,
      rightShell: value === 'matching' ? current.shell : (current.rightShell || current.shell)
    }));
    bindSegment('[data-pfg-v3-button-pair-mode]', 'buttonPairMode', (current, value) => ({
      ...current,
      buttonPairMode: value,
      rightButtonColor: value === 'matching' ? current.buttonColor : (current.rightButtonColor || current.buttonColor)
    }));
    bindSegment('[data-pfg-v3-angle]', 'angle');
    bindSegment('[data-pfg-v3-caps]', 'capColor');

    shellSelect.addEventListener('change', () => commit({ ...desiredState, pairMode: 'matching', shell: shellSelect.value, rightShell: shellSelect.value, family: catalog.getShell(shellSelect.value).family }), { signal });
    leftShellSelect.addEventListener('change', () => commit({ ...desiredState, pairMode: 'independent', shell: leftShellSelect.value, family: catalog.getShell(leftShellSelect.value).family }), { signal });
    rightShellSelect.addEventListener('change', () => commit({ ...desiredState, pairMode: 'independent', rightShell: rightShellSelect.value }), { signal });
    buttonSelect.addEventListener('change', () => commit({ ...desiredState, buttonPairMode: 'matching', buttonColor: buttonSelect.value, rightButtonColor: buttonSelect.value, accent: 'matching', faceStyle: 'aftermarket' }), { signal });
    leftButtonSelect.addEventListener('change', () => commit({ ...desiredState, buttonPairMode: 'independent', buttonColor: leftButtonSelect.value, accent: 'matching', faceStyle: 'aftermarket' }), { signal });
    rightButtonSelect.addEventListener('change', () => commit({ ...desiredState, buttonPairMode: 'independent', rightButtonColor: rightButtonSelect.value, accent: 'matching', faceStyle: 'aftermarket' }), { signal });
    designSelect.addEventListener('change', () => {
      if (designSelect.value !== 'none') artworkEditor.uploadSequence += 1;
      commit({ ...desiredState, design: designSelect.value });
    }, { signal });
    accentSelect.addEventListener('change', () => commit({ ...desiredState, accent: accentSelect.value, faceStyle: 'aftermarket' }), { signal });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      checkout?.submit();
    }, { signal });

    global.addEventListener('popstate', () => {
      const next = core.parsePreviewState(global.location.search, committedState);
      commit(next, { writeUrl: true, rollbackUrlOnFailure: true });
    }, { signal });

    const instance = Object.freeze({
      root,
      getState: () => ({ ...committedState }),
      getPendingState: () => ({ ...desiredState }),
      setState: (next) => commit({ ...desiredState, ...next }),
      loadArtworkPreview: (url, fileName) => artworkEditor.loadPreviewUrl(url, fileName),
      getArtworks: () => artworkEditor.getArtworks(),
      getCheckoutStatus: () => checkout?.getStatus(),
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        renderToken += 1;
        checkout?.destroy();
        artworkEditor.dispose();
        renderer.dispose();
        abort.abort();
        root.removeAttribute('data-pfg-v3-ready');
        instances.delete(root);
      }
    });

    const purchaseButton = root.querySelector('.pfg-sw1-builder-v3__submit');
    const purchaseStatus = root.querySelector('.pfg-sw1-builder-v3__cart-status');
    const reviewCart = root.querySelector('[data-pfg-v3-review-cart]');
    let lockedControls = null;
    if (global.PFGSw1BuilderV3Checkout) {
      checkout = global.PFGSw1BuilderV3Checkout.createCheckoutController({
        config: parseJson(root, '[data-pfg-sw1-checkout-config]', null),
        getState: () => ({...committedState}),
        getArtworks: () => artworkEditor.getArtworks(),
        getConfigurationURL: () => global.location.href,
        isRenderReady: () => !destroyed && root.dataset.pfgV3Ready === 'true' && stage.getAttribute('aria-busy') === 'false' && errorBox.hidden && !artworkEditor.loading,
        onChange: value => {
          if (value.busy && !lockedControls) {
            lockedControls = Array.from(form.querySelectorAll('input,select,button')).filter(control => control !== purchaseButton).map(control => [control, control.disabled]);
          }
          if (value.busy) lockedControls?.forEach(([control]) => {control.disabled = true;});
          else if (lockedControls) {
            lockedControls.forEach(([control, disabled]) => {control.disabled = disabled;});
            lockedControls = null;
          }
          purchaseButton.disabled = value.disabled;
          purchaseButton.setAttribute('aria-disabled', String(value.disabled));
          purchaseButton.setAttribute('aria-busy', String(value.busy));
          purchaseButton.textContent = value.busy ? 'Saving…' : value.recovery ? 'Check cart again' : 'Add build to cart';
          purchaseStatus.textContent = value.message;
          purchaseStatus.classList.toggle('is-error', value.error);
          if (reviewCart) {reviewCart.hidden = !(value.recovery && value.cartUrl); if(value.cartUrl) reviewCart.href = value.cartUrl;}
        }
      });
      checkout.initialize();
    } else purchaseStatus.textContent = 'Checkout could not load. Please refresh the page.';
    instances.set(root, instance);
    refreshControls(committedState);
    updateCopy(committedState);

    const requested = core.parsePreviewState(global.location.search, committedState);
    const expectedComposition = core.resolveLayers(requested).layers.map((asset) => asset.id).join('|');
    if (
      stack.childElementCount
      && stack.dataset.pfgV3Composition === expectedComposition
      && root.dataset.pfgV3ContractSignature === contractSignature
      && root.dataset.pfgV3AssetSignature === assetMapSignature
    ) {
      committedState = requested;
      desiredState = requested;
      root.dataset.pfgV3CommittedState = JSON.stringify(committedState);
      root.dataset.pfgV3ContractSignature = contractSignature;
      root.dataset.pfgV3AssetSignature = assetMapSignature;
      refreshControls(committedState);
      updateCopy(committedState);
      artworkEditor.sync(committedState);
      fallback.hidden = true;
      stack.hidden = false;
      loading.hidden = true;
      stage.setAttribute('aria-busy', 'false');
      root.dataset.pfgV3Ready = 'true';
      status.textContent = `${core.accessibilityLabel(committedState)} loaded.`;
    } else requestPreview(requested, { writeUrl: true, rollbackUrlOnFailure: true });
    return instance;
  }

  function init(scope = document) {
    if (moduleDisposed) return [];
    return rootsIn(scope).map(mount).filter(Boolean);
  }

  function destroy(scope = document) {
    rootsIn(scope).forEach((root) => instances.get(root)?.destroy());
  }

  function dispose() {
    if (moduleDisposed) return;
    moduleDisposed = true;
    Array.from(instances.values()).forEach((instance) => instance.destroy());
    moduleAbort.abort();
  }

  global.PFGSw1BuilderV3 = Object.freeze({
    version: VERSION,
    contractSignature,
    init,
    mount,
    destroy,
    dispose,
    diagnostics: () => {
      const sizes = Array.from(renderers, (renderer) => renderer.cache.size);
      return Object.freeze({
        imageCacheSize: sizes.reduce((total, size) => total + size, 0),
        imageCacheLimit: IMAGE_CACHE_LIMIT,
        maxRendererCacheSize: sizes.length ? Math.max(...sizes) : 0,
        rendererCount: sizes.length,
        imageLoadTimeoutMs: IMAGE_LOAD_TIMEOUT_MS
      });
    },
    Renderer: LayerStackRenderer
  });

  const moduleSignal = moduleAbort.signal;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true, signal: moduleSignal });
  } else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target), { signal: moduleSignal });
  document.addEventListener('shopify:section:select', (event) => init(event.target), { signal: moduleSignal });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (root) mount(root);
  }, { signal: moduleSignal });
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target), { signal: moduleSignal });
})(window, document);
